import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Building2, Loader2 } from "lucide-react";
import { useSwitchTenant } from "../hooks/useSwitchTenant";
import type { TenantMembership } from "../stores/authStore";
import {
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

interface TenantSwitcherMenuProps {
  memberships: TenantMembership[];
  activeTenantId: string | null;
  /** Called after a successful switch, once the store + query cache have settled. */
  onSwitched: () => void;
}

/**
 * Tenant picker inside the sidebar footer profile dropdown.
 *
 * Intentionally renders nothing when there is one membership or fewer — the
 * `UX_TenantMemberships_UserId_CompatSingle` partial unique index (AB#1470/F1)
 * keeps every user at exactly one live membership until AB#1711 (F5) lifts it,
 * so this is inert in production today by design, not a bug. The
 * `memberships.length > 1` branch only has real coverage via component tests
 * (see `TenantSwitcherMenu.test.tsx`) — there is no live multi-membership user
 * to browser-verify it against yet.
 */
export const TenantSwitcherMenu: React.FC<TenantSwitcherMenuProps> = ({
  memberships,
  activeTenantId,
  onSwitched,
}) => {
  const { t } = useTranslation();
  const switchTenant = useSwitchTenant();

  const handleValueChange = useCallback(
    (tenantId: string) => {
      if (tenantId === activeTenantId || switchTenant.isPending) return;
      switchTenant.mutate(tenantId, { onSuccess: onSwitched });
    },
    [activeTenantId, switchTenant, onSwitched],
  );

  if (memberships.length <= 1) return null;

  return (
    <>
      <DropdownMenuLabel className="flex items-center gap-1.5" aria-live="polite">
        <Building2 size={12} aria-hidden="true" />
        {switchTenant.isPending
          ? t("sidebar.profile.dropdown.switchTenant.switching")
          : t("sidebar.profile.dropdown.switchTenant.label")}
        {switchTenant.isPending && (
          <Loader2 size={12} className="ml-auto animate-spin" aria-hidden="true" />
        )}
      </DropdownMenuLabel>
      <DropdownMenuRadioGroup value={activeTenantId ?? undefined} onValueChange={handleValueChange}>
        {memberships.map((m) => (
          <DropdownMenuRadioItem
            key={m.tenantId}
            value={m.tenantId}
            disabled={switchTenant.isPending}
            className="gap-2"
            // Keep the dropdown mounted through the switch's async round trip — Radix
            // closes (and this component's async work with it) on select by default.
            // `onSwitched` closes the menu explicitly once the switch has actually landed.
            onSelect={(e) => e.preventDefault()}
          >
            <span className="truncate">{m.tenantName}</span>
          </DropdownMenuRadioItem>
        ))}
      </DropdownMenuRadioGroup>
      <DropdownMenuSeparator />
    </>
  );
};

export default TenantSwitcherMenu;
